import http from "@/services/http";
import Roles, { RoleCode, toRoleCode, toRoleName } from "@/constants/Roles";

/**
 * Modelo de usuario del backend (CRUD de usuarios)
 */
export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: Roles;
  phoneNumber?: string;
  dealerId?: string;
  active: boolean;
  createdAt?: string;
}

export interface UserListParams {
  search?: string | null;
  page?: number;
  pageSize?: number;
  isActive?: boolean | null;
  role?: Roles | null;
}

export interface UserCreateRequest {
  email: string;
  password: string;
  name: string;
  phoneNumber?: string;
  role: Roles;
  dealerId?: string;
}

export interface UserUpdateRequest {
  email?: string;
  name?: string;
  phoneNumber?: string;
  active?: boolean;
  role?: Roles;
  dealerId?: string | null;
}

export interface ChangeMyPasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface SetUserPasswordRequest {
  newPassword: string;
  confirmPassword: string;
}

export interface GenerateClientUserRequest {
  name: string;
  email?: string;
  phone?: string;
  loanId?: string;
  contractId?: string;
}

export interface GenerateClientUserResponse {
  userId: string;
  name: string;
  email: string;
  phone?: string;
  isSyntheticEmail: boolean;
  generatedPassword: string;
}

export interface UserListResponse {
  items: AdminUser[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

interface ApiAdminUser {
  id: string;
  email: string;
  name: string;
  role: RoleCode | string | number;
  phone?: string;
  phoneNumber?: string;
  dealerId?: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string | null;
}

interface ApiUserCreateRequest {
  email: string;
  password: string;
  name: string;
  phone?: string;
  role: RoleCode;
  dealerId?: string;
}

interface ApiUserUpdateRequest {
  email?: string;
  name?: string;
  phone?: string;
  active?: boolean;
  role?: RoleCode;
  dealerId?: string | null;
}

interface ApiChangeMyPasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ApiSetUserPasswordRequest {
  newPassword: string;
  confirmPassword: string;
}

interface ApiGenerateClientUserRequest {
  name: string;
  email?: string;
  phone?: string;
  loanId?: string;
  contractId?: string;
}

interface ApiGenerateClientUserResponse {
  userId: string;
  name: string;
  email: string;
  phone?: string;
  isSyntheticEmail: boolean;
  generatedPassword: string;
}

interface ApiUserListResponse {
  items?: ApiAdminUser[];
  page?: number;
  pageSize?: number;
  totalItems?: number;
  totalPages?: number;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

const normalizeUser = (user: ApiAdminUser): AdminUser => {
  const roleName = toRoleName(user.role) || Roles.Client;
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    role: roleName,
    phoneNumber: user.phone ?? user.phoneNumber,
    dealerId: user.dealerId,
    active: user.active,
    createdAt: user.createdAt,
  };
};

class UserService {
  async getMyProfile(): Promise<AdminUser> {
    const user = await http.get<ApiAdminUser>("/users/me");
    return normalizeUser(user);
  }

  async listUsers(params: UserListParams = {}): Promise<UserListResponse> {
    const query = new URLSearchParams();
    if (params.search) query.set("search", params.search);
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 10;
    query.set("page", String(page));
    query.set("pageSize", String(pageSize));
    if (params.isActive !== null && params.isActive !== undefined) {
      query.set("isActive", String(params.isActive));
    }
    if (params.role) {
      query.set("role", String(params.role));
    }

    const qs = query.toString();
    const response = await http.get<ApiAdminUser[] | ApiUserListResponse>(
      `/users${qs ? `?${qs}` : ""}`
    );

    if (Array.isArray(response)) {
      const items = response.map(normalizeUser);
      return {
        items,
        page,
        pageSize,
        totalItems: items.length,
        totalPages: 1,
        hasNext: false,
        hasPrevious: false,
      };
    }

    const items = Array.isArray(response?.items)
      ? response.items.map(normalizeUser)
      : [];
    return {
      items,
      page: response?.page || page,
      pageSize: response?.pageSize || pageSize,
      totalItems: response?.totalItems ?? items.length,
      totalPages: response?.totalPages ?? 1,
      hasNext: response?.hasNext ?? false,
      hasPrevious: response?.hasPrevious ?? false,
    };
  }

  async getUserById(userId: string): Promise<AdminUser> {
    const user = await http.get<ApiAdminUser>(`/users/${userId}`);
    return normalizeUser(user);
  }

  async createUser(data: UserCreateRequest): Promise<AdminUser> {
    const roleCode = toRoleCode(data.role);
    if (!roleCode) {
      throw { errorCode: "VALIDATION_ERROR" };
    }
    const payload: ApiUserCreateRequest = {
      email: data.email,
      password: data.password,
      name: data.name,
      role: roleCode,
      phone: data.phoneNumber || undefined,
      dealerId: data.dealerId || undefined,
    };
    const user = await http.post<ApiAdminUser>("/users", payload);
    return normalizeUser(user);
  }

  async updateUser(userId: string, data: UserUpdateRequest): Promise<AdminUser> {
    const roleCode = data.role ? toRoleCode(data.role) : null;
    if (data.role && !roleCode) {
      throw { errorCode: "VALIDATION_ERROR" };
    }
    const normalizedDealerId =
      data.dealerId === null ? null : data.dealerId || undefined;
    const payload: ApiUserUpdateRequest = {
      email: data.email,
      name: data.name,
      phone: data.phoneNumber || undefined,
      active: data.active,
      dealerId: normalizedDealerId,
    };
    if (roleCode) {
      payload.role = roleCode;
    }
    const user = await http.put<ApiAdminUser>(`/users/${userId}`, payload);
    return normalizeUser(user);
  }

  async updateUserRole(userId: string, role: Roles, dealerId?: string | null): Promise<void> {
    const roleCode = toRoleCode(role);
    if (!roleCode) {
      throw { errorCode: "VALIDATION_ERROR" };
    }
    const payload: { role: RoleCode; dealerId?: string | null } = { role: roleCode };
    if (dealerId !== undefined) {
      payload.dealerId = dealerId;
    }
    await http.put<void>(`/users/${userId}/role`, payload);
  }

  async changeMyPassword(data: ChangeMyPasswordRequest): Promise<void> {
    const payload: ApiChangeMyPasswordRequest = {
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
      confirmPassword: data.confirmPassword,
    };
    await http.post<void>("/users/change-password", payload);
  }

  async setUserPassword(userId: string, data: SetUserPasswordRequest): Promise<void> {
    const payload: ApiSetUserPasswordRequest = {
      newPassword: data.newPassword,
      confirmPassword: data.confirmPassword,
    };
    await http.put<void>(`/users/${userId}/password`, payload);
  }

  async generateClientUser(data: GenerateClientUserRequest): Promise<GenerateClientUserResponse> {
    const payload: ApiGenerateClientUserRequest = {
      name: data.name,
      email: data.email || undefined,
      phone: data.phone || undefined,
      loanId: data.loanId || undefined,
      contractId: data.contractId || undefined,
    };
    const response = await http.post<ApiGenerateClientUserResponse>("/users/generate-client", payload);
    return {
      userId: response.userId,
      name: response.name,
      email: response.email,
      phone: response.phone,
      isSyntheticEmail: response.isSyntheticEmail,
      generatedPassword: response.generatedPassword,
    };
  }

  async activateUser(userId: string): Promise<void> {
    await http.put<void>(`/users/${userId}/activate`);
  }

  async deactivateUser(userId: string): Promise<void> {
    await http.put<void>(`/users/${userId}/deactivate`);
  }

  async deleteUser(userId: string): Promise<void> {
    await http.delete<void>(`/users/${userId}`);
  }
}

export default new UserService();
